/**
 * Public job endpoints (anonymous, token in the path). Mirrors the contract in
 * axlerun-app#72 §4. The token is the 43-char Base64Url string from the URL.
 */

import { API_BASE_URL } from './config';

export interface VehicleLocation {
  latitude: number;
  longitude: number;
  heading?: number | null;
  speedKmh?: number | null;
  recordedAtUtc?: string | null;
}

export interface PublicJobLocation {
  addressLine1?: string | null;
  suburb?: string | null;
  state?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface PublicJob {
  jobId: string;
  title?: string | null;
  description?: string | null;
  status?: string | null;
  scheduledStartUtc?: string | null;
  scheduledEndUtc?: string | null;
  customerName?: string | null;
  location?: PublicJobLocation | null;
  vehicleLocation?: VehicleLocation | null;
  /**
   * True only when the business runs the commentary feature AND allows customer
   * job-link viewing — the single flag the portal keys the thread off. Absent /
   * false on older backends, so the commentary UI simply doesn't render until
   * the backend ships it (safe forward-compat).
   */
  commentaryEnabled?: boolean;
}

/** An image/PDF attached to a thread message. `secureFileUrl` is a
 *  time-limited SAS URL — open it directly, don't cache. */
export interface PublicJobCommentAttachment {
  fileName?: string | null;
  contentType?: string | null;
  secureFileUrl?: string | null;
}

/**
 * One message in the customer-facing thread. Only `isCustomerFacing` entries
 * are ever returned here — internal driver/manager notes never cross the public
 * boundary. `authorLabel` is a display name the server chooses (e.g. the
 * business name, or "You" for the customer's own) — never a staff email.
 */
export interface PublicJobComment {
  jobCommentaryId: string;
  message?: string | null;
  isFromCustomer?: boolean;
  authorLabel?: string | null;
  createdAt?: string | null;
  attachments?: PublicJobCommentAttachment[] | null;
}

/**
 * Result union so the UI can branch on the documented states without throwing:
 *   ok      — 200, job data
 *   invalid — 404 (unknown/expired token)   → "invalid link" screen
 *   done    — 410 (completed/revoked)        → "job complete" screen
 *   error   — network or unexpected status   → retryable error screen
 *
 * Contract confirmed in api#45: 404 = invalid/unknown/expired/revoked,
 * 410 = job completed/cancelled. 403 is kept mapped to `invalid` as a harmless
 * defensive fallback (earlier sandbox builds answered 403 for a bad token).
 */
export type JobResult =
  | { kind: 'ok'; job: PublicJob }
  | { kind: 'invalid' }
  | { kind: 'done' }
  | { kind: 'error' };

export async function fetchJob(token: string): Promise<JobResult> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/public/job/${encodeURIComponent(token)}`);
    if (res.status === 200) return { kind: 'ok', job: (await res.json()) as PublicJob };
    if (res.status === 410) return { kind: 'done' };
    if (res.status === 404 || res.status === 403) return { kind: 'invalid' };
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

/** Live location poll. Returns null on any non-200 (not yet en route, no GPS). */
export async function fetchLocation(token: string): Promise<VehicleLocation | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/public/job/${encodeURIComponent(token)}/location`);
    if (res.status !== 200) return null;
    return (await res.json()) as VehicleLocation;
  } catch {
    return null;
  }
}

/**
 * Customer-facing commentary thread for the job. Returns null on any non-200 so
 * the UI hides the thread rather than erroring — covers a backend that hasn't
 * shipped the endpoint yet, commentary being disabled (403), or a bad token.
 * Backend: GET /api/public/job/{token}/commentary
 */
export async function fetchCommentary(token: string): Promise<PublicJobComment[] | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/public/job/${encodeURIComponent(token)}/commentary`);
    if (res.status !== 200) return null;
    return (await res.json()) as PublicJobComment[];
  } catch {
    return null;
  }
}

export type PostCommentResult =
  | { ok: true; comment: PublicJobComment }
  | { ok: false; rateLimited?: boolean; status?: number; detail?: string };

/** Image/PDF types the customer may attach, mirroring the app's own limits. */
export const ATTACH_ACCEPT = 'image/jpeg,image/png,image/heic,application/pdf';
export const ATTACH_MAX_BYTES = 10 * 1024 * 1024; // 10 MB per file, per the app

/** Server caps the message at 2000 chars (api#46). */
export const COMMENT_MAX_CHARS = 2000;

/**
 * Add a message to the thread as the customer, optionally with photo/PDF
 * attachments (camera capture or file pick). Always multipart/form-data — the
 * server binds `message` from a form field (api#46), so text-only is a form
 * with just that field, not a JSON body. The server stamps isFromCustomer +
 * isCustomerFacing + sourceChannel and rate-limits (6/min per IP → 429).
 * Backend: POST /api/public/job/{token}/commentary  (message + attachments[])
 */
export async function postComment(
  token: string,
  message: string,
  files: File[] = [],
): Promise<PostCommentResult> {
  const url = `${API_BASE_URL}/api/public/job/${encodeURIComponent(token)}/commentary`;
  try {
    const form = new FormData();
    form.append('message', message);
    // Field name mirrors the authenticated endpoint (`attachments`).
    for (const f of files) form.append('attachments', f, f.name);
    const res = await fetch(url, { method: 'POST', body: form });
    if (res.status === 200 || res.status === 201) {
      return { ok: true, comment: (await res.json()) as PublicJobComment };
    }
    // Surface the server's reason so a failed send is diagnosable rather than a
    // blank "try again". Body is best-effort (may be empty / non-text).
    const detail = await res.text().catch(() => '');
    // eslint-disable-next-line no-console
    console.error(`postComment failed: HTTP ${res.status}`, detail);
    return { ok: false, rateLimited: res.status === 429, status: res.status, detail };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('postComment network error', err);
    return { ok: false };
  }
}
